import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, Gift, Send, Users, X } from 'lucide-react';
import { useReferrals } from '../../hooks/useReferrals';
import { useToast } from '../../contexts/ToastContext';
import { REFERRAL_CREDITS, buildReferralUrl, copyToClipboard } from '../../utils/referral';
interface SharePromptPopupProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens the full share sheet (image card, socials, PDF). */
  onOpenShareOptions: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function buildSecondOpinionMessage(url: string): string {
  return `I just ran a conversation through ToxicDetector. Want to give me a second opinion? Try it free — you'll get ${REFERRAL_CREDITS} free scans with my link: ${url}`;
}

/**
 * Post-scan prompt: invites the user to get a second opinion from a friend.
 * Shares the referral link so "Give 5. Get 5." rewards both people.
 */
export function SharePromptPopup({
  isOpen,
  onClose,
  onOpenShareOptions
}: SharePromptPopupProps) {
  const {
    referralCode,
    loading
  } = useReferrals();
  const {
    showToast
  } = useToast();
  const [copied, setCopied] = useState(false);
  const url = buildReferralUrl(referralCode);
  const message = buildSecondOpinionMessage(url);
  const handleCopy = async () => {
    const ok = await copyToClipboard(message);
    if (!ok) {
      showToast('We couldn’t copy the link. Please try again.', 'error');
      return;
    }
    setCopied(true);
    showToast('Link copied — send it to a friend.', 'success', 2500);
    window.setTimeout(() => setCopied(false), 2500);
  };
  const handleSendToFriend = async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'Want a second opinion?',
          text: message,
          url
        });
        onClose();
        return;
      } catch (err) {
        // User dismissed the share sheet — keep the prompt open.
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }
    await handleCopy();
  };
  return <AnimatePresence data-id="element-622" data-mp="3hry8hc14z260">
      {isOpen && <motion.aside role="dialog" aria-labelledby="share-prompt-title" aria-describedby="share-prompt-desc" initial={{
      opacity: 0,
      y: 16,
      scale: 0.96
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.96
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="fixed bottom-4 left-4 right-4 z-[60] rounded-2xl border border-forensic-line-strong bg-forensic-card p-5 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.8)] sm:bottom-6 sm:left-auto sm:right-6 sm:w-[384px]" data-id="element-623" data-mp="3ht9k1y0tpe7o">
          <button type="button" onClick={onClose} aria-label="Dismiss" className="absolute right-3 top-3 rounded-lg p-1.5 text-forensic-subtle transition-colors duration-150 ease-out hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-624" data-mp="ddgid4xi6fy7">
            <X size={16} data-id="element-625" data-mp="16vldvhbwaz3m" />
          </button>

          <div className="flex items-start gap-3 pr-6" data-id="element-626" data-mp="1azt45iy04n3y">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-forensic-line-strong bg-forensic-surface" data-id="element-627" data-mp="1ax6wljb427lq">
              <Users size={17} className="text-white" aria-hidden="true" data-id="element-628" data-mp="11xtmcof5xezy" />
            </span>
            <div data-id="element-629" data-mp="n9goflwilm2t">
              <h2 id="share-prompt-title" className="text-base font-semibold text-white" data-id="element-630" data-mp="n8x5c1v4orn7">
                Want a second opinion?
              </h2>
              <p id="share-prompt-desc" className="mt-1 text-sm leading-relaxed text-forensic-muted" data-id="element-631" data-mp="s29qg20lmgb7">
                Share your result with a friend.
              </p>
            </div>
          </div>

          <div className="mt-5 flex gap-2" data-id="element-632" data-mp="4auls2lcnmws">
            <button type="button" onClick={() => void handleSendToFriend()} disabled={loading} className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-forensic-crimson px-4 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-forensic-crimson-hover disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft focus-visible:ring-offset-2 focus-visible:ring-offset-forensic-card" data-id="element-633" data-mp="4bw1ikdb8xku">
              <Send size={15} aria-hidden="true" data-id="element-634" data-mp="sx47qd7wubb1" />
              Send to a friend
            </button>
            <button type="button" onClick={() => void handleCopy()} disabled={loading} aria-label={copied ? 'Link copied' : 'Copy link'} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-forensic-line-strong px-4 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-635" data-mp="ov0sddi4emkh">
              {copied ? <Check size={15} aria-hidden="true" data-id="element-636" data-mp="2ltg81xyeq5cb" /> : <Copy size={15} aria-hidden="true" data-id="element-637" data-mp="2lsev1nxtz8ry" />}
              <span className="whitespace-nowrap" data-id="element-638" data-mp="2qkg2mwk20bbn">{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>

          <button type="button" onClick={onOpenShareOptions} className="mt-3 w-full rounded-lg py-1.5 text-left text-sm text-forensic-muted transition-colors duration-150 ease-out hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-639" data-mp="2vk2kjjatufqs">
            Or{' '}
            <span className="font-medium text-white underline decoration-forensic-line-strong underline-offset-4" data-id="element-640" data-mp="27snebffxjea9">
              send this to someone who needs to see it
            </span>
          </button>

          <p className="mt-4 flex items-start gap-2 border-t border-forensic-line pt-4 text-xs leading-relaxed text-forensic-subtle" data-id="element-641" data-mp="3jb7whndncnvt">
            <Gift size={13} className="mt-0.5 shrink-0 text-amber-300" aria-hidden="true" data-id="element-642" data-mp="1o5fkrmrmkg3o" />
            <span data-id="element-643" data-mp="1j749r8plfzjv">
              <span className="font-semibold text-gray-200" data-id="element-644" data-mp="1j7nhjwlj57k6">
                Give {REFERRAL_CREDITS}. Get {REFERRAL_CREDITS}.
              </span>{' '}
              When your friend runs their first scan, you both get {REFERRAL_CREDITS} free scans.
            </span>
          </p>
        </motion.aside>}
    </AnimatePresence>;
}